import type { Category, Population } from "@entities";
import {
  Autocomplete,
  Box,
  Chip,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { compareBySortOrder } from "@shared/helpers/category-sort";
import { linko } from "@shared/linko";
import { useQuery } from "@tanstack/react-query";
import { useMemo, type FC } from "react";
import { useOrganization } from "../../../contexts/OrganizationProvider";
import type { StatisticsFilters } from "../types";

interface FilterOption {
  id: string;
  name: string;
  parentTeamId?: string | null;
}

interface StatisticsSegmentFiltersProps {
  filters: StatisticsFilters;
  onChange: (filters: StatisticsFilters) => void;
}

/**
 * Segment filters (category / sub-category / project / population) for scoped
 * queries such as real-time KPI - not shown as a global page filter.
 */
export const StatisticsSegmentFilters: FC<StatisticsSegmentFiltersProps> = ({
  filters,
  onChange,
}) => {
  const { currOrganization } = useOrganization();

  const categories = useMemo(
    () =>
      (currOrganization?.categories ?? [])
        .slice()
        .sort(compareBySortOrder),
    [currOrganization?.categories],
  );

  const populations = useMemo(
    () =>
      (currOrganization?.populations ?? [])
        .slice()
        .sort((a, b) => a.name.localeCompare(b.name)),
    [currOrganization?.populations],
  );

  const { data: teams = [] } = useQuery<FilterOption[]>({
    queryKey: ["statistics-teams", currOrganization?.id],
    queryFn: async () => {
      const { data } = await linko.statistics.queries.getTeams({
        organizationId: currOrganization!.id,
      });
      return data;
    },
    enabled: !!currOrganization?.id,
    staleTime: 1000 * 60 * 5,
  });

  const { data: subCategories = [] } = useQuery<FilterOption[]>({
    queryKey: [
      "statistics-subcategories",
      currOrganization?.id,
      filters.categoryIds,
    ],
    queryFn: async () => {
      const { data } = await linko.statistics.queries.getSubCategories({
        organizationId: currOrganization!.id,
        categoryIds: filters.categoryIds.length
          ? filters.categoryIds
          : undefined,
      });
      return data;
    },
    enabled: !!currOrganization?.id,
    staleTime: 1000 * 60 * 5,
  });

  const selectedCategories = categories.filter((c) =>
    filters.categoryIds.includes(c.id),
  );
  const selectedPopulations = populations.filter((p) =>
    filters.populationIds.includes(p.id),
  );
  const selectedTeams = teams.filter((t) => filters.teamIds.includes(t.id));
  const selectedSubCategory =
    subCategories.find((sc) => sc.id === filters.subCategoryId) ?? null;

  return (
    <Box sx={{ width: "100%" }}>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
        סינון לפי קטגוריה, פרויקט ואוכלוסייה (משפיע על נתוני זמן אמת בלבד).
        בחירת פרויקט כוללת גם את תתי-הפרויקטים שלו.
      </Typography>
      <Stack
        direction={{ xs: "column", md: "row" }}
        flexWrap="wrap"
        gap={1.5}
        alignItems={{ md: "flex-start" }}
      >
        <Autocomplete<Category, true>
          multiple
          size="small"
          sx={{ minWidth: { xs: "100%", md: 200 }, flex: { md: "1 1 180px" } }}
          options={categories}
          getOptionLabel={(o) => o.name}
          value={selectedCategories}
          onChange={(_, val) =>
            onChange({
              ...filters,
              categoryIds: val.map((c) => c.id),
              subCategoryId: null,
            })
          }
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                key={option.id}
                label={option.name}
                size="small"
              />
            ))
          }
          renderInput={(params) => (
            <TextField {...params} label="קטגוריה" />
          )}
          disableCloseOnSelect
        />

        <Autocomplete<FilterOption>
          size="small"
          sx={{ minWidth: { xs: "100%", md: 200 }, flex: { md: "0 1 200px" } }}
          options={subCategories}
          getOptionLabel={(o) => o.name}
          value={selectedSubCategory}
          onChange={(_, val) =>
            onChange({ ...filters, subCategoryId: val?.id ?? null })
          }
          renderInput={(params) => (
            <TextField {...params} label="תת-קטגוריה" />
          )}
        />

        <Autocomplete<FilterOption, true>
          multiple
          size="small"
          sx={{ minWidth: { xs: "100%", md: 200 }, flex: { md: "1 1 180px" } }}
          options={teams}
          getOptionLabel={(o) => o.name}
          value={selectedTeams}
          onChange={(_, val) =>
            onChange({ ...filters, teamIds: val.map((t) => t.id) })
          }
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                key={option.id}
                label={option.name}
                size="small"
              />
            ))
          }
          renderInput={(params) => <TextField {...params} label="פרויקט" />}
          disableCloseOnSelect
        />

        <Autocomplete<Population, true>
          multiple
          size="small"
          sx={{ minWidth: { xs: "100%", md: 200 }, flex: { md: "1 1 180px" } }}
          options={populations}
          getOptionLabel={(o) => o.name}
          value={selectedPopulations}
          onChange={(_, val) =>
            onChange({ ...filters, populationIds: val.map((p) => p.id) })
          }
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                key={option.id}
                label={option.name}
                size="small"
              />
            ))
          }
          renderInput={(params) => (
            <TextField {...params} label="אוכלוסייה" />
          )}
          disableCloseOnSelect
        />
      </Stack>
    </Box>
  );
};
